import React from "react";
import { useParams, Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import { useSeo } from "../lib/seo";
import { useTheme } from "../lib/theme";
import { getResource } from "../lib/resources";
import { ArrowLeft } from "lucide-react";

export default function ResourceArticle() {
  const { slug } = useParams();
  const article = getResource(slug);
  const { theme } = useTheme();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const articleUrl = `${origin}/resources/${slug}`;

  useSeo({
    title: article
      ? article.seoTitle || `${article.title} — Lecis Auto Glass`
      : "Resource Not Found — Lecis Auto Glass",
    description: article?.seoDescription || article?.excerpt || "Auto glass guide from Lecis Auto Glass.",
    canonicalPath: `/resources/${slug}`,
    robots: article ? "index,follow" : "noindex,follow",
    jsonLd: article
      ? {
          article: {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.seoDescription || article.excerpt,
            url: articleUrl,
            mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
            inLanguage: "en-US",
            publisher: { "@type": "Organization", name: "Lecis Auto Glass", url: origin },
          },
        }
      : undefined,
  });

  return (
    <StorefrontLayout>
      <article className="max-w-3xl mx-auto px-6 lg:px-12 py-16 md:py-24" data-testid="resource-article-page">
        <Link
          to="/resources"
          data-testid="resource-back-link"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition mb-10"
        >
          <ArrowLeft size={15} /> All Resources
        </Link>

        {article ? (
          <>
            {article.category && (
              <div className="text-[10px] uppercase tracking-[0.4em] text-red-500 font-bold mb-6">
                {article.category}
              </div>
            )}
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-bold mb-6">
              {article.title}
            </h1>
            {article.updated && (
              <div className="text-xs text-zinc-500 mb-10">Updated {article.updated}</div>
            )}
            <div
              className={`prose max-w-none ${theme === "dark" ? "prose-invert text-zinc-300" : "text-zinc-800"}`}
              data-testid="resource-article-body"
            >
              {article.body}
            </div>
          </>
        ) : (
          <div className="border border-white/10 rounded-md p-10 text-center" data-testid="resource-not-found">
            <h1 className="font-display text-2xl mb-3">Resource not found</h1>
            <p className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
              This article isn't available. Browse our resource library or reach out for fitment help.
            </p>
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 mt-8 bg-red-600 hover:bg-red-700 text-white px-5 h-11 text-sm rounded-sm transition"
            >
              Back to Resources
            </Link>
          </div>
        )}
      </article>
    </StorefrontLayout>
  );
}
